import { Application, Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { Reel } from './Reel.ts';
import { WinPresenter } from './WinPresenter.ts';
import { loadSymbolTextures, BG_URL } from '../theme/symbols.ts';
import { sfx } from '../audio/sfx.ts';
import type { SpinResponse } from '../provider/ProviderClient.ts';

const REELS = 5;
const ROWS = 3;
const CELL = 120;
const BOARD_W = REELS * CELL;
const BOARD_H = ROWS * CELL;
const PAD = 18;

const MIN_SPIN_MS = 460;
const STAGGER_MS = 130;
const ANTICIPATION_MS = 900;

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

/**
 * Pixi board: 5 reels that animate one provider spin result (server-authoritative).
 * Portrait-first: a themed tomb backdrop fills the iframe, the board scales to fill
 * width and sits in the upper-center so the DOM HUD frames it like a real slot.
 */
export class SlotGame {
  private app = new Application();
  private bgSprite = new Sprite();
  private bg = new Graphics();
  private root = new Container();
  private reels: Reel[] = [];
  private paylines: number[][] = [];
  private textures: Record<string, Texture> = {};
  private presenter!: WinPresenter;
  private bet = 0;

  private parent!: HTMLElement;

  async init(parent: HTMLElement): Promise<void> {
    this.parent = parent;
    await this.app.init({
      antialias: true,
      backgroundAlpha: 0,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      autoDensity: true,
      width: parent.clientWidth || 360,
      height: parent.clientHeight || 640,
    });
    parent.appendChild(this.app.canvas);

    // Load art before building the board.
    this.textures = await loadSymbolTextures();
    try {
      this.bgSprite.texture = await Assets.load<Texture>(BG_URL);
    } catch {
      /* background is decorative; fall back to the gradient below */
    }
    this.bgSprite.anchor.set(0.5);

    this.app.stage.addChild(this.bgSprite);
    this.app.stage.addChild(this.bg);
    this.app.stage.addChild(this.root);
    this.buildFrame();
    this.buildReels();
    this.presenter = new WinPresenter({
      cell: CELL,
      reelsCount: REELS,
      rows: ROWS,
      reels: this.reels,
      ticker: this.app.ticker,
      textures: this.textures,
      fmt: (n) => (Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2)),
    });
    this.presenter.mount(this.root);
    this.app.ticker.add((t) => {
      for (const r of this.reels) r.update(t.deltaMS);
    });

    // Manual sizing (resizeTo races with iframe layout → tiny board). Observe the
    // parent and re-fit on every size change, plus a couple of delayed re-fits.
    const ro = new ResizeObserver(() => this.fit());
    ro.observe(parent);
    window.addEventListener('resize', () => this.fit());
    this.fit();
    requestAnimationFrame(() => this.fit());
    setTimeout(() => this.fit(), 300);
  }

  setPaylines(paylines: number[][]): void {
    this.paylines = paylines || [];
    this.presenter?.setPaylines(this.paylines);
  }

  setBet(bet: number): void {
    this.bet = bet;
  }

  private buildFrame(): void {
    // Ornate double frame around the reel area.
    const g = new Graphics();
    g.roundRect(-PAD - 6, -PAD - 6, BOARD_W + (PAD + 6) * 2, BOARD_H + (PAD + 6) * 2, 26)
      .fill({ color: 0x1a0f04, alpha: 0.96 })
      .stroke({ width: 4, color: 0xffcf57, alpha: 0.9 });
    g.roundRect(-PAD, -PAD, BOARD_W + PAD * 2, BOARD_H + PAD * 2, 20)
      .stroke({ width: 1.5, color: 0xffe9a8, alpha: 0.35 });
    g.roundRect(-2, -2, BOARD_W + 4, BOARD_H + 4, 12)
      .fill({ color: 0x0c0905, alpha: 0.92 });
    this.root.addChild(g);

    const sep = new Graphics();
    for (let i = 1; i < REELS; i++) {
      sep.rect(i * CELL - 1, 0, 2, BOARD_H).fill({ color: 0xffcf57, alpha: 0.08 });
    }
    this.root.addChild(sep);
  }

  private buildReels(): void {
    for (let i = 0; i < REELS; i++) {
      const reel = new Reel(CELL, this.textures);
      reel.view.position.set(i * CELL, 0);
      this.reels.push(reel);
      this.root.addChild(reel.view);
    }
  }

  private drawBg(width: number, height: number): void {
    // Cover-fit the tomb backdrop, then dim it so the reels stay readable.
    if (this.bgSprite.texture && this.bgSprite.texture.width) {
      const t = this.bgSprite.texture;
      const scale = Math.max(width / t.width, height / t.height);
      this.bgSprite.scale.set(scale);
      this.bgSprite.position.set(width / 2, height / 2);
      this.bgSprite.visible = true;
    } else {
      this.bgSprite.visible = false;
    }
    this.bg.clear();
    this.bg.rect(0, 0, width, height).fill({ color: 0x0a0602, alpha: 0.45 });
    // Top + bottom vignette so the HUD and title sit on darkness.
    this.bg.rect(0, 0, width, height * 0.2).fill({ color: 0x000000, alpha: 0.45 });
    this.bg.rect(0, height * 0.62, width, height * 0.38).fill({ color: 0x000000, alpha: 0.55 });
  }

  private fit(): void {
    const w = this.parent.clientWidth || window.innerWidth || 360;
    const h = this.parent.clientHeight || window.innerHeight || 640;
    this.app.renderer.resize(w, h);
    this.drawBg(w, h);
    // Scale off WIDTH so the board can never collapse to a sliver; cap the height
    // so it clears the bottom controls overlay.
    let scale = (w * 0.94) / BOARD_W;
    const maxBoardH = h * 0.6;
    if (BOARD_H * scale > maxBoardH) scale = maxBoardH / BOARD_H;
    this.root.scale.set(scale);
    this.root.position.set((w - BOARD_W * scale) / 2, Math.max(74, h * 0.15));
  }

  /** Animate one authoritative provider spin. Resolves with true if it was a win. */
  async spin(result: SpinResponse): Promise<boolean> {
    this.presenter.cancel();
    for (const r of this.reels) r.start();
    await delay(MIN_SPIN_MS);

    // Land the PRE-expansion symbols on expanded reels; the expand animation
    // paints them afterwards. (Old servers send no preReels → painted grid.)
    const landing = result.reels.map((column, i) => {
      const at = result.expanded?.preReels ? result.expanded.reels.indexOf(i) : -1;
      return at >= 0 ? result.expanded!.preReels![at] : column;
    });

    // Stop schedule: 2+ scatters on stopped reels → tease the rest (capped).
    const stopDelays: number[] = [];
    const anticAfter: boolean[] = [];
    let delayAcc = 0;
    let books = 0;
    let extensions = 0;
    for (let i = 0; i < REELS; i++) {
      stopDelays.push(delayAcc);
      books += landing[i].filter((s) => s === 'book').length;
      const anticipating = books >= 2 && i < REELS - 1 && extensions < 2;
      anticAfter.push(anticipating);
      delayAcc += anticipating ? STAGGER_MS + ANTICIPATION_MS : STAGGER_MS;
      if (anticipating) extensions++;
    }

    await Promise.all(
      this.reels.map(
        (reel, i) =>
          new Promise<void>((res) => {
            setTimeout(() => {
              reel.stop(landing[i], res);
              if (anticAfter[i]) {
                sfx.anticipation();
                for (let j = i + 1; j < REELS; j++) this.reels[j].setAnticipation(true);
              }
            }, stopDelays[i]);
          }),
      ),
    );

    if (result.expanded) await this.presenter.playExpand(result);

    const won = result.win > 0 || (result.winningLines?.length ?? 0) > 0;
    if (won) await this.presenter.present(result, this.bet);
    return won;
  }

  clearWins(): void {
    for (const r of this.reels) r.clearWinning();
  }

  get busy(): boolean {
    return this.reels.some((r) => r.spinning);
  }
}
